import { Injector, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule } from '@angular/router';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { AppComponent } from './app.component';
import { appRoutes } from './app.routes';
import { NxWelcomeComponent } from './nx-welcome.component';
import { MaterialModule } from './material.module';
import { LucideAngularModule, Activity, Settings, Box, LogOut, ChevronDown, Check, FileText, Fuel, Package, CreditCard, RefreshCw, Search, Filter, X, History, Edit, DollarSign, ChevronLeft, ChevronRight, Receipt, Banknote, Trash2, Save, Clock, CheckCircle, Users, User, Ship, FileSignature, ShoppingCart, Anchor, Globe, ShieldAlert, AlertCircle, Mail } from 'lucide-angular';
import { SidebarComponent } from './shared/components/sidebar/sidebar.component';
import { UiModule, SAFE_NOOP_ANIMATION_PROVIDERS, AlertService } from '@marina/ui';
import {
  AUTH_READINESS_PROVIDERS,
  AUTH_SESSION_UI_CLEANUP,
  AuthInterceptor,
  ErrorInterceptor,
  AuthService,
} from '@marina/auth';
import { MARINA_ID_PROVIDER, IMarinaIdProvider, API_CONFIG, PaymentsHubService, InAppNotificationsStateService, LaunchBoardHubService, VesselsHubService, ContractsHubService } from '@marina/api-client';
import { environment } from '../environments/environment';

// Provide AuthService as IMarinaIdProvider
const marinaIdProviderFactory = (authService: AuthService): IMarinaIdProvider => {
  return {
    getCurrentMarinaId: () => authService.getCurrentMarinaId(),
  };
};

@NgModule({
  declarations: [AppComponent, NxWelcomeComponent, SidebarComponent],
  imports: [
    BrowserModule,
    // Material overlay/sidenav players race with CD: "Cannot read properties of undefined (reading 'startTime')".
    // Per-page [@.disabled] does not cover CDK overlays or unpatched routes. No custom Angular triggers exist.
    NoopAnimationsModule,
    HttpClientModule,
    RouterModule.forRoot(appRoutes),
    MaterialModule,
    UiModule,
    LucideAngularModule.pick({ Activity, Settings, Box, LogOut, ChevronDown, Check, FileText, Fuel, Package, CreditCard, RefreshCw, Search, Filter, X, History, Edit, DollarSign, ChevronLeft, ChevronRight, Receipt, Banknote, Trash2, Save, Clock, CheckCircle, Users, User, Ship, FileSignature, ShoppingCart, Anchor, Globe, ShieldAlert, AlertCircle, Mail }),
  ],
  providers: [
    ...SAFE_NOOP_ANIMATION_PROVIDERS,
    // Never dismiss dialogs by backdrop/Escape by default — form dialogs prompt on explicit close.
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        disableClose: true,
        enterAnimationDuration: 0,
        exitAnimationDuration: 0,
      } satisfies MatDialogConfig,
    },
    // API Configuration
    {
      provide: API_CONFIG,
      useValue: {
        baseUrl: environment.apiUrl,
        apiVersion: 'v1',
        timeout: 30000,
      },
    },
    // Dialogs and snackbars render outside the router outlet, so the session-end navigation leaves
    // them on screen — a modal from the previous page floating over the login form, often offering
    // an action the user no longer has a session for.
    {
      provide: AUTH_SESSION_UI_CLEANUP,
      multi: true,
      useFactory: (
        dialog: MatDialog,
        snackBar: MatSnackBar,
        alerts: AlertService,
        injector: Injector
      ) => ({
        closeOverlays: () => {
          dialog.closeAll();
          snackBar.dismiss();
          // The action toasts are their own overlay host, not MatSnackBar, so dismissing the
          // snack bar does not touch them. Left behind, a failure toast from the request that
          // ended the session sits on top of the login page telling the user to retry something
          // they are no longer signed in for.
          alerts.dismissAll();
        },
        // Hubs are resolved here, not as factory deps: they inject ENSURE_FRESH_CREDENTIALS,
        // which needs AuthService, which needs the terminator, which needed this token — NG0200.
        stopRealtime: () => {
          void injector.get(PaymentsHubService).disconnect();
          void injector.get(InAppNotificationsStateService).stop();
          void injector.get(LaunchBoardHubService).disconnect();
          void injector.get(VesselsHubService).disconnect();
          void injector.get(ContractsHubService).disconnect();
        },
      }),
      deps: [MatDialog, MatSnackBar, AlertService, Injector],
    },
    // Hub connections bypass the HTTP interceptors, so they need their own way to wait for
    // a current access token before negotiating.
    ...AUTH_READINESS_PROVIDERS,

    // HTTP Interceptors — ORDER IS LOAD-BEARING.
    // Angular builds the chain with reduceRight over this array, so the first entry is OUTERMOST:
    // it sees requests first and errors LAST. ErrorInterceptor must therefore come first, leaving
    // AuthInterceptor inside it where it receives the raw HttpErrorResponse.
    // With these two swapped, ErrorInterceptor converted every error to an ApiError (whose field is
    // `statusCode`, not `status`) before AuthInterceptor saw it, so its `status === 401` test never
    // matched and its entire refresh-and-retry path was dead code — a recoverable 401 logged the
    // user out instead, once per failed request.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    // Marina ID Provider
    {
      provide: MARINA_ID_PROVIDER,
      useFactory: marinaIdProviderFactory,
      deps: [AuthService],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
